import moment from 'moment-timezone';

// Date and time formats can be localized
export const DATE_FORMAT: string = 'L';                                     // Ex: 12/13/2019
export const TIME_FORMAT: string = 'LT';                                    // Ex: 8:17 AM
export const DATE_TIME_FORMAT: string = 'LT L';                             // Ex: 8:17 AM 12/13/2019
export const TIME_DATE_FORMAT: string = 'L LT';                             // Ex: 12/13/2019 8:17 AM
export const DATE_TIME_FULL_FORMAT: string = 'LLLL';                        // Ex: Friday, December 13, 2019 8:17 AM

export function stringToDate(stringToDate: string): Date {
    return moment(stringToDate).toDate();
}

// DateTime.Min is 01/01/0001 and unix min is 01/01/1970.
// Anything greater than that should be valid for display.
export function dateIsValidForDisplay(dateToCheck: Date): boolean {
    return new Date(dateToCheck) > new Date(1971, 0, 0, 0, 0, 0, 0);
}

// Default formatting to use to get local time
export function passlyFormatDate(dateToFormat: Date | string, format: string): string {
    return moment(dateToFormat).format(format);
}

// Get time zone of local time
export function passlyFormatDateWithTimeZoneName(dateToFormat: Date | string): string {
    return moment.tz(dateToFormat, Intl.DateTimeFormat().resolvedOptions().timeZone).zoneName();
}

// Formatting to use to get the UTC offset
export function passlyFormatDateUtc(dateToFormat: Date, format: string): string {
    return moment(dateToFormat).utc().format(format);
}

// Converts the unix epoch in milliseconds into displayable local time
export function passlyFormatUnixMilliseconds(unixTimestampToFormat: number, format: string): string {
    return moment.unix(unixTimestampToFormat / 1000).format(format);
}

// Converts the unix epoch in seconds into displayable local time
export function passlyFormatUnixSeconds(unixTimestampToFormat: number, format: string): string {
    return moment.unix(unixTimestampToFormat).format(format);
}

export function passlyTimeDuration(dateString: string): number {
    return moment.duration(dateString).asSeconds();
}

export function passlyTimeDurationAsMinutes(dateString: string): number {
    return moment.duration(dateString).asMinutes();
}

export function passlyHumanizeDate(dateString: string): string {
    return moment.duration(dateString).humanize();
}

export function parseTimeSpan (ts: string, unit: string): number {
    let duration: any = moment.duration(ts);

    if (duration._days <= 0 && duration._milliseconds <= 0) {
        return 0;
    }

    if (duration._days) {
        return duration._days;
    }

    duration = duration._milliseconds / 1000 / 60;

    if (unit === 'days') {
        duration = duration / 60 / 24;
    }

    return duration;
}

export function toTimeSpan (time: any, unit: any) {
    if (unit === 'ticks') {
        return parseInt(time) * 24 * 60 * 60 * 1000 * 10000;
    }

    return moment.duration(parseInt(time), unit).toString();
}

export function fromNowMoment(dt: any) {
    return moment(dt).fromNow();
}

export function fromNow(date: any) {
    const dateTime = moment(date);
    let msPerMinute = 60 * 1000;
    let msPerHour = msPerMinute * 60;
    let msPerDay = msPerHour * 24;
    let msPer2Days = msPerHour * 24 * 2;
    let msPerWeek = msPerHour * 24 * 7;
    let msPerYear = msPerDay * 365;

    let yesterday = moment().subtract(1, 'day');
    const isYesterday = dateTime.isSame(yesterday, 'day');
    let elapsed = moment().diff(date);

    if (elapsed > 0 && elapsed < msPerMinute) {
        return `Just now`;
    } else if (elapsed < 0) {
        return fromNowMoment(date);
    }

    else if (elapsed < msPerHour) {
        return Math.round(elapsed / msPerMinute) + `m ago`;
    }

    else if (elapsed < msPerDay ) {
        return dateTime.format('h:mm a');
    }

    else if (isYesterday) {
        return dateTime.format('[Yesterday], h:mm a');
    }

    else if (elapsed > msPer2Days && elapsed < msPerWeek) {
        return Math.round(elapsed / msPerDay) + ' days ago';
    }

    else if (elapsed > msPerWeek && elapsed < msPerYear) {
        return dateTime.format('D MMM');
    }

    else {
        return dateTime.format('YYYY');
    }
}